import { SVGProps, useContext, useEffect, useMemo, useRef, useState } from "react"
import { CanvasControlsContext } from "@/components/canvas-controls/CanvasControlsContext"
import { Icon } from "../icons/Icon"
import { projectImageTransformCoords, unprojectPointerCoord } from "./canvas-controls-utils"
import { HealingItem } from "@/core/types"
import { useDebounce } from "@/utils/useDebounce"
import { calcBoundingBox } from "@/core/utils/brushing"
import { Rectangle } from "@/core/types"
import {
	getHealingTransformRectBoundary,
	HealingTransformBoundsContext,
} from "@/core/utils/healing-transform-bounds"

interface HealingEdgeProps extends SVGProps<SVGForeignObjectElement> {
	canvasRef: React.RefObject<HTMLCanvasElement | null>
	healingItem: HealingItem
	hideDestination: boolean
	hideSource: boolean
	eventPosition?: [number, number]
	hidden: boolean
	projectionContext: HealingTransformBoundsContext
	onEventInsideHandler: (
		healingItem: HealingItem,
		areaType: "source" | "destination" | null,
	) => void
}

const tintColor = "rgba(255,255,255,1.0)"
const setupContext = (ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D) => {
	ctx.lineCap = "round"
	ctx.lineJoin = "round"
}

const transformRect = (rect: Rectangle, context: HealingTransformBoundsContext) => {
	const boundary = getHealingTransformRectBoundary(rect, context)
	let transformed = boundary?.flatMap((point) => [point[0], point[1]])
	if (!transformed) {
		const samples: number[] = []
		const steps = 16
		for (let index = 0; index <= steps; index++) {
			const t = index / steps
			samples.push(rect.x + rect.width * t, rect.y)
		}
		for (let index = 1; index <= steps; index++) {
			const t = index / steps
			samples.push(rect.x + rect.width, rect.y + rect.height * t)
		}
		for (let index = 1; index <= steps; index++) {
			const t = index / steps
			samples.push(rect.x + rect.width * (1 - t), rect.y + rect.height)
		}
		for (let index = 1; index < steps; index++) {
			const t = index / steps
			samples.push(rect.x, rect.y + rect.height * (1 - t))
		}
		transformed = projectImageTransformCoords(samples, context) ?? undefined
	}
	if (!transformed || transformed.length === 0) return null
	let minX = Number.POSITIVE_INFINITY
	let minY = Number.POSITIVE_INFINITY
	let maxX = Number.NEGATIVE_INFINITY
	let maxY = Number.NEGATIVE_INFINITY
	for (let i = 0; i < transformed.length; i += 2) {
		const x = transformed[i] ?? 0
		const y = transformed[i + 1] ?? 0
		minX = Math.min(minX, x)
		minY = Math.min(minY, y)
		maxX = Math.max(maxX, x)
		maxY = Math.max(maxY, y)
	}
	return {
		rect: {
			x: minX,
			y: minY,
			width: maxX - minX,
			height: maxY - minY,
		},
		points: transformed,
	}
}

export const HealingEdge = ({
	canvasRef,
	healingItem,
	hideDestination,
	hideSource,
	eventPosition,
	hidden,
	projectionContext,
	onEventInsideHandler,
}: HealingEdgeProps) => {
	const { parentWidth, parentHeight } = useContext(CanvasControlsContext)

	const [showArrow, setShowArrow] = useState(false)
	// offsets from the destination center
	const [linePositionOffsets, setLinePositionOffsets] = useState<
		[number, number, number, number] | null
	>(null)
	const [sourceBlob, setSourceBlob] = useState<string | null>(null)
	const sourceBlobRef = useRef<string | null>(null)
	const [edgePath, setEdgePath] = useState<Path2D | null>(null)
	const [maxDistanceFromSourceCenter, setMaxDistanceFromSourceCenter] = useState(0)

	useEffect(() => {
		return () => {
			if (sourceBlobRef.current) {
				URL.revokeObjectURL(sourceBlobRef.current)
			}
		}
	}, [])

	// AI draft items render synchronously via draftStrokePath; non-draft items use the blob.
	const isAiTempItem = healingItem.aiVariations?.length === 0 && healingItem.mode !== "healing"

	const sourceCenter = healingItem.sourceCenter
	const destCenter = healingItem.destinationCenter
	const inputPoints = useMemo(() => healingItem.strokes[0]?.points ?? [], [healingItem.strokes])
	const projectedInputPoints = useMemo(() => {
		return projectImageTransformCoords(inputPoints, projectionContext)
	}, [inputPoints, projectionContext])
	const transformedInputPoints = projectedInputPoints ?? inputPoints
	const singlePoint = inputPoints.length === 2

	let strokeWidth = healingItem.strokes[0]?.size ?? 1
	strokeWidth *= Math.min(parentWidth, parentHeight)
	const destRect = healingItem.strokesRect
	const transformedStroke = healingItem.strokes[0]
		? [{ ...healingItem.strokes[0], points: transformedInputPoints }]
		: []
	const transformedDestRect =
		transformedStroke.length > 0
			? calcBoundingBox(transformedStroke, parentHeight / parentWidth)
			: destRect

	const isAiItem = Boolean(healingItem.aiVariations)
	const fullRect = healingItem.aiVariations?.[healingItem.selectedVariation ?? 0]?.fullRect
	const shouldUseRectangleEdge = isAiItem && healingItem.applyBrushMask === false && !!fullRect
	const transformedFullRect = useMemo(() => {
		return fullRect ? transformRect(fullRect, projectionContext) : null
	}, [fullRect, projectionContext])

	// SVG path for the AI draft highlight, in absolute viewport coords.
	const draftStrokePath = useMemo(() => {
		if (transformedInputPoints.length < 2) return ""
		if (singlePoint) {
			const x = (transformedInputPoints[0] ?? 0) * parentWidth
			const y = (transformedInputPoints[1] ?? 0) * parentHeight
			return `M ${x} ${y} L ${x} ${y}`
		}
		let d = ""
		for (let i = 0; i < transformedInputPoints.length; i += 2) {
			const x = (transformedInputPoints[i] ?? 0) * parentWidth
			const y = (transformedInputPoints[i + 1] ?? 0) * parentHeight
			d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`
		}
		return d
	}, [transformedInputPoints, singlePoint, parentWidth, parentHeight])

	const iconSize = 14
	// Use the appropriate rect for center calculations
	const centerRect = shouldUseRectangleEdge
		? (transformedFullRect?.rect ?? fullRect)
		: transformedDestRect
	const transformedCenters = projectImageTransformCoords(
		[...destCenter, ...sourceCenter],
		projectionContext,
	)
	const transformedDestCenter: [number, number] = [
		transformedCenters?.[0] ?? destCenter[0],
		transformedCenters?.[1] ?? destCenter[1],
	]
	const transformedSourceCenter: [number, number] = [
		transformedCenters?.[2] ?? sourceCenter[0],
		transformedCenters?.[3] ?? sourceCenter[1],
	]
	const projectionFailed =
		projectedInputPoints === null ||
		transformedCenters === null ||
		(shouldUseRectangleEdge && transformedFullRect === null)
	const destCenterXoffset = (transformedDestCenter[0] - centerRect.x) * parentWidth
	const destCenterYoffset = (transformedDestCenter[1] - centerRect.y) * parentHeight
	const sourceOffsetVec2 = [
		(transformedSourceCenter[0] - transformedDestCenter[0]) * parentWidth,
		(transformedSourceCenter[1] - transformedDestCenter[1]) * parentHeight,
	] as [number, number]
	const angle = Math.atan2(-sourceOffsetVec2[1], -sourceOffsetVec2[0])
	const rectangleEdgePath = useMemo(() => {
		if (!transformedFullRect?.points) {
			return ""
		}
		let d = ""
		for (let i = 0; i < transformedFullRect.points.length; i += 2) {
			const x = (transformedFullRect.points[i] ?? 0) * parentWidth
			const y = (transformedFullRect.points[i + 1] ?? 0) * parentHeight
			d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`
		}
		return `${d} Z`
	}, [parentHeight, parentWidth, transformedFullRect?.points])

	const updateBlob = useDebounce(() => {
		const cx = (centerRect.width / 2) * parentWidth
		const cy = (centerRect.height / 2) * parentHeight

		let strokeWidth = healingItem.strokes[0]?.size ?? 1
		strokeWidth *= Math.min(parentWidth, parentHeight)
		let maxDistanceFromSourceCenter = 0

		const destinationPath = new Path2D()

		if (singlePoint) {
			const x = ((transformedInputPoints[0] ?? 0) - centerRect.x) * parentWidth
			const y = ((transformedInputPoints[1] ?? 0) - centerRect.y) * parentHeight
			destinationPath.arc(x, y, 0.001, 0, 2 * Math.PI)
			maxDistanceFromSourceCenter = strokeWidth / 2
			strokeWidth -= 2
		} else {
			let x = 0
			let y = 0
			for (let i = 0; i < transformedInputPoints.length; i += 2) {
				x = ((transformedInputPoints[i] ?? 0) - centerRect.x) * parentWidth
				y = ((transformedInputPoints[i + 1] ?? 0) - centerRect.y) * parentHeight

				if (i === 0) {
					destinationPath.moveTo(x, y)
				} else {
					destinationPath.lineTo(x, y)
				}

				const distance = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2)
				if (distance > maxDistanceFromSourceCenter) {
					maxDistanceFromSourceCenter = distance
				}
			}
			maxDistanceFromSourceCenter += strokeWidth / 2
		}

		// Set geometry synchronously so hit-detection doesn't wait on the raster encode.
		setMaxDistanceFromSourceCenter(maxDistanceFromSourceCenter)
		setEdgePath(destinationPath)
		if (!edgePath) {
			computeEventInside(destinationPath)
		}

		// Draft items render via SVG; the blob is only needed for non-draft edges.
		if (isAiTempItem) {
			return
		}

		const canvasWidth = parentWidth * centerRect.width
		const canvasHeight = parentHeight * centerRect.height
		const canvas = new OffscreenCanvas(canvasWidth, canvasHeight)
		const ctx = canvas.getContext("2d")
		if (!ctx) {
			return
		}
		ctx.save()
		ctx.clearRect(0, 0, canvasWidth, canvasHeight)
		setupContext(ctx)
		drawOutlines(ctx, destinationPath, strokeWidth)
		ctx.restore()

		canvas.convertToBlob().then((blob) => {
			if (sourceBlobRef.current) {
				URL.revokeObjectURL(sourceBlobRef.current)
			}
			const url = URL.createObjectURL(blob)
			sourceBlobRef.current = url
			setSourceBlob(url)
		})
	}, 150)

	const drawOutlines = (
		ctx: OffscreenCanvasRenderingContext2D,
		edgePath: Path2D,
		strokeWidth: number,
	) => {
		ctx.save()
		// Black outline at full strokeWidth - 1 (fits within canvas bounds)
		ctx.lineWidth = strokeWidth - 1
		ctx.strokeStyle = `rgba(0,0,0,0.5)`
		ctx.globalCompositeOperation = "source-over"
		ctx.stroke(edgePath)

		// White line - smaller to show ~1px of black on each side
		ctx.lineWidth = strokeWidth - 3
		ctx.strokeStyle = tintColor
		ctx.globalCompositeOperation = "source-over"
		ctx.stroke(edgePath)

		// Cut out center to make hollow
		ctx.lineWidth = strokeWidth - 5
		ctx.globalCompositeOperation = "destination-out"
		ctx.stroke(edgePath)
		ctx.restore()
	}

	const isPointInStroke = (
		canvas: HTMLCanvasElement | null,
		x: number,
		y: number,
		edgePath: Path2D | null,
		offset?: [number, number],
	) => {
		let isInStroke = false
		if (!canvas) {
			return isInStroke
		}

		const ctx = canvas.getContext("2d")
		if (!ctx) {
			return isInStroke
		}

		if (offset) {
			x -= offset[0]
			y -= offset[1]
		}

		if (shouldUseRectangleEdge && edgePath) {
			ctx.save()
			setupContext(ctx)
			ctx.lineWidth = 10
			isInStroke = ctx.isPointInPath(edgePath, x, y) || ctx.isPointInStroke(edgePath, x, y)
			ctx.restore()
		} else if (singlePoint) {
			const distance = Math.sqrt((x - destCenterXoffset) ** 2 + (y - destCenterYoffset) ** 2)
			isInStroke = distance <= strokeWidth / 2
		} else if (edgePath) {
			ctx.save()
			setupContext(ctx)
			ctx.lineWidth = strokeWidth
			isInStroke = ctx.isPointInStroke(edgePath, x, y)
			ctx.restore()
		}

		return isInStroke
	}

	const findIntersection = (
		pointA: [number, number],
		stepOffset: [number, number],
		isFindToSource: boolean,
	): [number, number] => {
		const stepPoint = [pointA[0] + stepOffset[0], pointA[1] + stepOffset[1]] as [number, number]

		// prevent infinite loop
		if (
			stepPoint[0] > parentWidth ||
			stepPoint[0] < -parentWidth ||
			stepPoint[1] > parentHeight ||
			stepPoint[1] < -parentHeight
		) {
			return pointA
		}

		if (
			isPointInStroke(
				canvasRef.current,
				stepPoint[0],
				stepPoint[1],
				edgePath,
				isFindToSource ? sourceOffsetVec2 : undefined,
			)
		) {
			return pointA
		} else {
			return findIntersection(stepPoint, stepOffset, isFindToSource)
		}
	}

	const computeIntersection = () => {
		const stepDistance = Math.min(strokeWidth, iconSize / 2)
		const extendsRadius = maxDistanceFromSourceCenter + (singlePoint ? 2 : stepDistance * 2)

		const stepDirection: [number, number] = [
			stepDistance * Math.cos(angle),
			stepDistance * Math.sin(angle),
		]

		const startDestX = destCenterXoffset + sourceOffsetVec2[0] + Math.cos(angle) * extendsRadius
		const startDestY = destCenterYoffset + sourceOffsetVec2[1] + Math.sin(angle) * extendsRadius

		const startSrcX = destCenterXoffset - Math.cos(angle) * extendsRadius
		const startSrcY = destCenterYoffset - Math.sin(angle) * extendsRadius

		let intersectionToDest: [number, number]

		let intersectionToSrc: [number, number]

		if (singlePoint) {
			intersectionToDest = [startSrcX, startSrcY]
			intersectionToSrc = [startDestX, startDestY]
		} else {
			intersectionToDest = findIntersection(
				[startSrcX, startSrcY],
				[stepDirection[0], stepDirection[1]],
				false,
			)
			intersectionToSrc = findIntersection(
				[startDestX, startDestY],
				[-stepDirection[0], -stepDirection[1]],
				true,
			)
		}

		const lineOffsetX = intersectionToSrc[0] - intersectionToDest[0]
		const lineOffsetY = intersectionToSrc[1] - intersectionToDest[1]

		const lineDistance = Math.sqrt(lineOffsetX ** 2 + lineOffsetY ** 2)
		if (
			Math.sign(lineOffsetX) !== Math.sign(sourceOffsetVec2[0]) ||
			Math.sign(lineOffsetY) !== Math.sign(sourceOffsetVec2[1]) ||
			lineDistance < iconSize * 0.5
		) {
			setLinePositionOffsets(null)
			setShowArrow(false)
		} else {
			setLinePositionOffsets([
				intersectionToSrc[0] / parentWidth,
				intersectionToSrc[1] / parentHeight,
				intersectionToDest[0] / parentWidth,
				intersectionToDest[1] / parentHeight,
			])

			if (lineDistance > iconSize * 0.7) {
				setShowArrow(true)
			} else {
				setShowArrow(false)
			}
		}
	}

	const computeEventInside = (edgePath: Path2D) => {
		if (!eventPosition) {
			return
		}
		const pointerStart = unprojectPointerCoord(eventPosition[0], eventPosition[1])

		// Use the correct rect based on edge type
		pointerStart[0] -= centerRect.x
		pointerStart[1] -= centerRect.y

		const isInDest =
			!hideDestination &&
			isPointInStroke(
				canvasRef.current,
				pointerStart[0] * parentWidth,
				pointerStart[1] * parentHeight,
				edgePath,
			)
		const isInSource =
			isAiItem || hideSource
				? false
				: isPointInStroke(
						canvasRef.current,
						pointerStart[0] * parentWidth,
						pointerStart[1] * parentHeight,
						edgePath,
						sourceOffsetVec2,
					)
		onEventInsideHandler(healingItem, isInDest ? "destination" : isInSource ? "source" : null)
	}

	useEffect(() => {
		if (projectionFailed || !edgePath) {
			setLinePositionOffsets(null)
			setShowArrow(false)
			return
		}
		if (!hidden) {
			computeIntersection()
		}
	}, [sourceCenter, destCenter, edgePath, hidden, projectionFailed])

	useEffect(() => {
		if (projectionFailed) {
			setEdgePath(null)
			setLinePositionOffsets(null)
			setShowArrow(false)
			if (sourceBlobRef.current) {
				URL.revokeObjectURL(sourceBlobRef.current)
				sourceBlobRef.current = null
			}
			setSourceBlob(null)
			return
		}
		if (shouldUseRectangleEdge) {
			// For rectangles, update immediately (no debounce needed)
			const renderRect = centerRect
			const canvasWidth = parentWidth * renderRect.width
			const canvasHeight = parentHeight * renderRect.height

			const rectanglePath = new Path2D()
			const rectPoints = transformedFullRect?.points
			if (rectPoints) {
				for (let i = 0; i < rectPoints.length; i += 2) {
					const x = ((rectPoints[i] ?? 0) - centerRect.x) * parentWidth
					const y = ((rectPoints[i + 1] ?? 0) - centerRect.y) * parentHeight
					if (i === 0) {
						rectanglePath.moveTo(x, y)
					} else {
						rectanglePath.lineTo(x, y)
					}
				}
				rectanglePath.closePath()
			} else {
				rectanglePath.rect(0, 0, canvasWidth, canvasHeight)
			}
			setEdgePath(rectanglePath)
			setMaxDistanceFromSourceCenter(Math.max(canvasWidth, canvasHeight) / 2)
			setSourceBlob(null)

			// Trigger hit detection immediately for rectangles
			if (eventPosition) {
				computeEventInside(rectanglePath)
			}
		} else {
			// For brush strokes, build geometry (and the blob for non-draft items) via debounce
			updateBlob()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isAiTempItem,
		parentHeight,
		parentWidth,
		projectedInputPoints,
		projectionFailed,
		shouldUseRectangleEdge,
		transformedFullRect,
	])

	useEffect(() => {
		if (projectionFailed || !eventPosition || !edgePath) {
			return
		}
		computeEventInside(edgePath)
	}, [eventPosition, projectionFailed])

	if (hidden || projectionFailed) {
		return null
	}

	// Use the correct rect based on edge type
	const imgWidth = centerRect.width * parentWidth
	const imgHeight = centerRect.height * parentHeight

	return (
		<g>
			{shouldUseRectangleEdge ? (
				// Rectangle edge with same styling as brush paths (shadow + white stroke)
				<>
					{!hideDestination && rectangleEdgePath && (
						<>
							{/* Black shadow stroke */}
							<path
								d={rectangleEdgePath}
								fill="none"
								stroke="rgba(0,0,0,0.5)"
								strokeWidth={2}
							/>
							{/* White main stroke */}
							<path
								d={rectangleEdgePath}
								fill="none"
								stroke={tintColor}
								strokeWidth={1.5}
							/>
						</>
					)}
				</>
			) : (
				<>
					{isAiTempItem ? (
						// AI draft: render the painted region as a hollow outline synchronously.
						!hideDestination &&
						draftStrokePath !== "" && (
							<>
								<mask
									id={`healing-draft-mask-${healingItem.id}`}
									maskUnits="userSpaceOnUse"
									x={0}
									y={0}
									width={parentWidth}
									height={parentHeight}
								>
									<rect
										x={0}
										y={0}
										width={parentWidth}
										height={parentHeight}
										fill="white"
									/>
									{/* Punch out the interior to make the stroke hollow */}
									<path
										d={draftStrokePath}
										fill="none"
										stroke="black"
										strokeWidth={Math.max(0, strokeWidth - 5)}
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</mask>
								<g mask={`url(#healing-draft-mask-${healingItem.id})`}>
									<path
										d={draftStrokePath}
										fill="none"
										stroke="rgba(0,0,0,0.5)"
										strokeWidth={Math.max(0, strokeWidth - 1)}
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path
										d={draftStrokePath}
										fill="none"
										stroke={tintColor}
										strokeWidth={Math.max(0, strokeWidth - 3)}
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</g>
							</>
						)
					) : (
						<>
							{!hideSource && sourceBlob && (
								<image
									x={centerRect.x * parentWidth + sourceOffsetVec2[0]}
									y={centerRect.y * parentHeight + sourceOffsetVec2[1]}
									width={imgWidth}
									height={imgHeight}
									href={sourceBlob}
								/>
							)}

							{!hideDestination && sourceBlob && (
								<image
									x={centerRect.x * parentWidth}
									y={centerRect.y * parentHeight}
									width={imgWidth}
									height={imgHeight}
									href={sourceBlob}
								/>
							)}
						</>
					)}
					{!hideSource && linePositionOffsets && (
						<>
							<line
								x1={(linePositionOffsets[0] + centerRect.x) * parentWidth}
								y1={(linePositionOffsets[1] + centerRect.y) * parentHeight}
								x2={(linePositionOffsets[2] + centerRect.x) * parentWidth}
								y2={(linePositionOffsets[3] + centerRect.y) * parentHeight}
								stroke={tintColor}
								strokeWidth={1}
							/>
							{showArrow && (
								<g
									transform={`
									translate(${(linePositionOffsets[2] + centerRect.x) * parentWidth - iconSize / 2},
									${(linePositionOffsets[3] + centerRect.y) * parentHeight - iconSize / 2})
									rotate(${angle * (180 / Math.PI)} ${iconSize / 2} ${iconSize / 2})`}
								>
									<Icon
										className="pointer-events-none"
										name={"right"}
										size={iconSize}
										color={tintColor}
									/>
								</g>
							)}
						</>
					)}
				</>
			)}
		</g>
	)
}
